import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle, Braces, FlaskConical, Gauge, Layers3, Play, Save, Loader2,
} from 'lucide-react'
import {
  useCreateRule, useRule, useRuleFields, useStartRun, useTestRule,
  useTlds, useUpdateRule, useValidateRule,
} from '@/lib/queries'
import { ApiError, ErrorCode } from '@/lib/api'
import type { Estimate, RuleDocument, RuleGroup, RuleNode, TestResult } from '@/lib/types'
import { isGroup } from '@/lib/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Input, Textarea } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import {
  MAX_NODES, RuleNodeEditor, countNodes, defaultValueFor, preferredOperator, treeDepth,
} from '@/features/rule-builder/RuleTree'
import { GuidedRuleBuilder } from '@/features/rule-builder/GuidedRuleBuilder'
import { cn, formatCompact, formatDuration, formatNumber } from '@/lib/utils'

const EMPTY_RULE: RuleDocument = {
  schema_version: 1,
  name: '',
  mode: 'continuous',
  candidate_budget: 1_000_000,
  root: {
    type: 'group',
    op: 'AND',
    not: false,
    children: [
      { type: 'condition', field: 'tld', operator: 'IN', value: ['xyz'], id: 'seed-tld' },
    ],
  },
  schedule: { trigger: ['zone_diff', 'periodic'], interval_minutes: 360 },
}

export default function RuleEditorPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id
  const navigate = useNavigate()
  const toast = useToast()

  const fieldsQuery = useRuleFields()
	const tldsQuery = useTlds()
  const ruleQuery = useRule(id ?? null)
  const validate = useValidateRule()
  const create = useCreateRule()
  const update = useUpdateRule()
  const startRun = useStartRun()
  const test = useTestRule(id ?? null)

  const [doc, setDoc] = React.useState<RuleDocument>(EMPTY_RULE)
  const [name, setName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [version, setVersion] = React.useState(1)
  const [status, setStatus] = React.useState('draft')
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [samples, setSamples] = React.useState('123321.xyz\n121212.xyz\n456456.xyz\nexample.com')
  const [testResults, setTestResults] = React.useState<TestResult[] | null>(null)
  const [dirty, setDirty] = React.useState(false)
  const validationSequence = React.useRef(0)

  // Load an existing rule into the editor exactly once.
  React.useEffect(() => {
    if (!ruleQuery.data) return
    setDoc(ruleQuery.data.rule_json)
    setName(ruleQuery.data.name)
    setDescription(ruleQuery.data.description)
    setVersion(ruleQuery.data.version)
    setStatus(ruleQuery.data.status)
    setDirty(false)
  }, [ruleQuery.data])

	const fields = React.useMemo(() => {
		const enabledTlds = (tldsQuery.data?.tlds ?? [])
			.filter((item) => item.enabled)
			.map((item) => ({ value: item.tld, label: `.${item.tld}` }))
		return (fieldsQuery.data?.fields ?? []).map((field) =>
			field.name === 'tld' ? { ...field, enum: enabledTlds } : field,
		)
	}, [fieldsQuery.data?.fields, tldsQuery.data?.tlds])
  const nodeCount = countNodes(doc.root)
  const depth = treeDepth(doc.root)
  const labelLength = exactLabelLength(doc.root)

  function updateScheduleInterval(value: string) {
    setDoc((d) => ({
      ...d,
      schedule: {
        ...d.schedule,
        trigger: d.schedule?.trigger ?? ['zone_diff', 'periodic'],
        interval_minutes: Number(value),
      },
    }))
    setDirty(true)
  }

  /** Applies an immutable update at a JSONPath, deleting when the updater returns null. */
  const changeNode = React.useCallback(
    (path: string, updater: (node: RuleNode) => RuleNode | null) => {
      setDirty(true)
      setDoc((prev) => {
        const next = structuredClone(prev)
        if (path === '$.root') {
          const updated = updater(next.root)
          if (updated && isGroup(updated)) next.root = updated
          return next
        }
        const { parent, index } = resolveParent(next.root, path)
        if (!parent || index < 0) return next
        const updated = updater(parent.children[index])
        if (updated === null) parent.children.splice(index, 1)
        else parent.children[index] = updated
        return next
      })
    },
    [],
  )

  const addCondition = React.useCallback(
    (path: string, fieldName?: string) => {
      setDirty(true)
      setDoc((prev) => {
        const next = structuredClone(prev)
        const group = path === '$.root' ? next.root : (resolveNode(next.root, path) as RuleGroup | null)
        if (!group || !isGroup(group)) return next
        const spec = fields.find((field) => field.name === fieldName)
          ?? fields.find((field) => !field.deferred)
        const operator = preferredOperator(spec)
        group.children.push({
          type: 'condition',
          field: spec?.name ?? 'tld',
          operator,
          value: defaultValueFor(spec, operator),
          id: crypto.randomUUID(),
        })
        return next
      })
    },
    [fields],
  )

  const addGroup = React.useCallback((path: string) => {
    setDirty(true)
    setDoc((prev) => {
      const next = structuredClone(prev)
      const group = path === '$.root' ? next.root : (resolveNode(next.root, path) as RuleGroup | null)
      if (!group || !isGroup(group)) return next
      group.children.push({
        type: 'group', op: 'OR', not: false, children: [], id: crypto.randomUUID(),
      })
      return next
    })
  }, [])

  const duplicateNode = React.useCallback((path: string) => {
    setDirty(true)
    setDoc((prev) => {
      const next = structuredClone(prev)
      const { parent, index } = resolveParent(next.root, path)
      if (!parent || index < 0) return next
      const duplicate = structuredClone(parent.children[index])
      duplicate.id = crypto.randomUUID()
      parent.children.splice(index + 1, 0, duplicate)
      return next
    })
  }, [])

  const payload = React.useMemo<RuleDocument>(
    () => ({ ...doc, name: name || doc.name || '未命名规则' }),
    [doc, name],
  )

  React.useEffect(() => {
    if (!dirty && !isNew) return
    const sequence = ++validationSequence.current
    const timer = window.setTimeout(async () => {
      try {
        const result = await validate.mutateAsync({ rule_json: payload, name })
        if (sequence !== validationSequence.current) return
        setErrors(result.errors?.length
          ? Object.fromEntries(result.errors.map((error) => [error.path, error.message]))
          : {})
      } catch (error) {
        if (sequence !== validationSequence.current || !(error instanceof ApiError)) return
        setErrors(error.fieldErrors.length
          ? Object.fromEntries(error.fieldErrors.map((item) => [item.path, item.message]))
          : {})
      }
    }, 600)
    return () => window.clearTimeout(timer)
  }, [dirty, isNew, name, payload])

  const treeContext = {
    fields,
    errors,
    onChange: changeNode,
    onAddCondition: addCondition,
    onAddGroup: addGroup,
    onDuplicate: duplicateNode,
    nodeCount,
    labelLength,
  }

  async function runValidation(): Promise<boolean> {
    setErrors({})
    try {
      const res = await validate.mutateAsync({ rule_json: payload, name })
      if (!res.valid && res.errors?.length) {
        setErrors(Object.fromEntries(res.errors.map((e) => [e.path, e.message])))
        scrollToRuleError(res.errors[0].path)
        toast.error('规则校验未通过', `${res.errors.length} 处问题，已在对应条件上标注`)
        return false
      }
      if (res.estimate?.reject_reason) {
        toast.error('规则成本过高', res.estimate.reject_reason)
        return false
      }
      toast.success('规则校验通过')
      return true
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.fieldErrors.length) {
          setErrors(Object.fromEntries(err.fieldErrors.map((e) => [e.path, e.message])))
          scrollToRuleError(err.fieldErrors[0].path)
        }
        toast.error(
          err.code === ErrorCode.RuleTooExpensive ? '规则成本过高' : '校验失败',
          err.message,
        )
      }
      return false
    }
  }

  async function save(nextStatus?: string) {
    if (!(await runValidation())) return
    try {
      if (isNew) {
        const res = await create.mutateAsync({
          rule_json: payload, name: name || '未命名规则', description,
          status: nextStatus ?? status,
        })
        toast.success('规则已创建', `版本 ${res.rule.version}`)
        setDirty(false)
        navigate(`/rules/${res.rule.id}`, { replace: true })
      } else {
        const res = await update.mutateAsync({
          id: id!, version,
          body: { rule_json: payload, name, description, status: nextStatus ?? status },
        })
        setVersion(res.rule.version)
        setStatus(res.rule.status)
        setDirty(false)
        toast.success('规则已保存', `版本 ${res.rule.version}`)
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === ErrorCode.VersionConflict) {
        // §5.6 asks specifically for an optimistic-lock conflict prompt.
        toast.error('保存冲突', `${err.message}。请刷新后重新编辑。`)
        return
      }
      toast.error('保存失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  async function runTest() {
    try {
      const res = await test.mutateAsync({
        samples: samples.split('\n').map((s) => s.trim()).filter(Boolean),
        rule_json: payload,
      })
      setTestResults(res.results)
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors.length) {
        const nextErrors = Object.fromEntries(err.fieldErrors.map((item) => [item.path, item.message]))
        setErrors(nextErrors)
        scrollToRuleError(err.fieldErrors[0].path)
        toast.error('测试前请先修正规则', `${err.fieldErrors.length} 处问题，已定位到对应条件`)
        return
      }
      toast.error('测试失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  if (fieldsQuery.isLoading || tldsQuery.isLoading || (!isNew && ruleQuery.isLoading)) {
    return (
      <>
        <PageHeader title={isNew ? '新建规则' : '编辑规则'} />
        <LoadingState rows={6} />
      </>
    )
  }
  if (ruleQuery.error) {
    return (
      <>
        <PageHeader title="编辑规则" />
        <ErrorState error={ruleQuery.error} onRetry={() => void ruleQuery.refetch()} />
      </>
    )
  }
	if (tldsQuery.error) {
		return (
			<div className="space-y-6">
				<PageHeader title="规则编辑器" />
				<ErrorState error={tldsQuery.error} onRetry={() => void tldsQuery.refetch()} />
			</div>
		)
	}

  // Older and seed-created rules may carry an empty JSON object as their
  // plan_summary. TypeScript types cannot make persisted JSON trustworthy at
  // runtime, so only pass a structurally complete estimate to the panel.
  const estimate = usableEstimate(validate.data?.estimate)
    ?? usableEstimate(ruleQuery.data?.plan_summary)
  const busy = create.isPending || update.isPending

  return (
    <>
      <PageHeader
        title={isNew ? '新建规则' : `编辑规则 · v${version}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge variant={status === 'active' ? 'success' : 'muted'}>{statusLabel(status)}</Badge>
            <span>
              {nodeCount}/{MAX_NODES} 个节点 · 嵌套深度 {depth}/5
            </span>
            {dirty && <Badge variant="warning">未保存</Badge>}
          </span>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => void runValidation()} disabled={validate.isPending}>
              {validate.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Gauge className="h-3.5 w-3.5" aria-hidden />}
              校验与估算
            </Button>
            <Button variant="outline" size="sm" onClick={() => void save('draft')} disabled={busy}>
              <Save className="h-3.5 w-3.5" aria-hidden /> 保存草稿
            </Button>
            <Button size="sm" onClick={() => void save('active')} disabled={busy}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Play className="h-3.5 w-3.5" aria-hidden />}
              保存并启用
            </Button>
            {!isNew && (
              <Button
                variant="secondary" size="sm"
                disabled={startRun.isPending || dirty}
                onClick={async () => {
                  try {
                    const res = await startRun.mutateAsync(id!)
                    toast.success('已排入运行队列', `run_id: ${res.run_id}`)
                  } catch (err) {
                    toast.error('无法启动运行', err instanceof ApiError ? err.message : undefined)
                  }
                }}
              >
                立即运行
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
        <div className="min-w-0 space-y-4">
          <Card>
            <CardContent className="grid gap-4 pt-5 sm:grid-cols-2 xl:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="rule-name">规则名称</Label>
                <Input
                  id="rule-name" value={name}
                  onChange={(e) => { setName(e.target.value); setDirty(true) }}
                  placeholder="例如：六位数字模式 .xyz"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rule-mode">运行方式</Label>
                <select
                  id="rule-mode" value={doc.mode}
                  onChange={(e) => {
                    setDoc((d) => ({ ...d, mode: e.target.value as 'query' | 'continuous' }))
                    setDirty(true)
                  }}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="query">即时查询（只查现有域名库）</option>
                  <option value="continuous">持续扫描（对新候选与 Zone Diff 运行）</option>
                </select>
              </div>
              {doc.mode === 'continuous' && (
                <div className="space-y-1.5">
                  <Label htmlFor="rule-interval">每轮间隔（分钟）</Label>
                  <Input
                    id="rule-interval"
                    type="number"
                    min={1}
                    step={1}
                    value={doc.schedule?.interval_minutes ?? 360}
                    onChange={(e) => updateScheduleInterval(e.target.value)}
                    onInput={(e) => updateScheduleInterval(e.currentTarget.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    本轮完成后等待该时长，再开始下一轮周期扫描。
                  </p>
                </div>
              )}
              <details className="group rounded-lg border bg-muted/20 sm:col-span-2 xl:col-span-3">
                <summary className="cursor-pointer list-none px-3 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground">
                  更多设置：说明
                </summary>
                <div className="border-t p-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="rule-desc">说明</Label>
                    <Textarea
                      id="rule-desc" value={description} rows={2}
                      onChange={(e) => { setDescription(e.target.value); setDirty(true) }}
                      placeholder="这条规则用来做什么"
                    />
                  </div>
                </div>
              </details>
            </CardContent>
          </Card>

          <Tabs defaultValue="guided">
            <TabsList>
              <TabsTrigger value="guided">
                <Layers3 className="mr-1 h-3.5 w-3.5" aria-hidden /> 引导模式
              </TabsTrigger>
              <TabsTrigger value="advanced">高级逻辑</TabsTrigger>
              <TabsTrigger value="json">
                <Braces className="mr-1 h-3.5 w-3.5" aria-hidden /> JSON 视图
              </TabsTrigger>
            </TabsList>

            <TabsContent value="guided">
              <GuidedRuleBuilder root={doc.root} ctx={treeContext} />
            </TabsContent>

            <TabsContent value="advanced">
              <RuleNodeEditor
                node={doc.root}
                path="$.root"
                depth={1}
                index={0}
                siblingCount={1}
                ctx={treeContext}
              />
            </TabsContent>

            <TabsContent value="json">
              <Card>
                <CardHeader>
                  <CardTitle>规则 JSON（只读）</CardTitle>
                  <CardDescription>
                    构建器与 JSON 互相可逆；如需直接编辑，请使用管理员/高级模式并重新校验。
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <pre className="scroll-x max-h-[520px] overflow-y-auto rounded-md bg-muted p-3 text-xs">
                    <code>{JSON.stringify(payload, null, 2)}</code>
                  </pre>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </div>

        <aside className="space-y-4">
          <EstimatePanel
            estimate={estimate}
            notice={validate.data?.notice}
            hasExternal={validate.data?.has_external_conditions}
            pending={validate.isPending}
          />
          <RuleTestPanel
            samples={samples}
            onSamplesChange={setSamples}
            onRun={() => void runTest()}
            pending={test.isPending}
            results={testResults}
          />
        </aside>
      </div>
    </>
  )
}

function EstimatePanel({
  estimate, notice, hasExternal, pending,
}: {
  estimate?: import('@/lib/types').Estimate
  notice?: string
  hasExternal?: boolean
  pending?: boolean
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>成本与命中估算</CardTitle>
        <CardDescription>
          上界为保守可判定估算；预期命中量级来自蒙特卡洛抽样，附置信区间。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {pending ? (
          <LoadingState rows={3} label="正在估算" />
        ) : !estimate ? (
          <p className="text-xs text-muted-foreground">点击“校验与估算”查看候选空间与预期命中。</p>
        ) : (
          <>
            {estimate.reject_reason && (
              <p role="alert" className="rounded-md bg-destructive/10 p-2.5 text-xs text-destructive">
                {estimate.reject_reason}
              </p>
            )}

            <div className="space-y-2">
              <Metric
                label="候选空间上界"
                value={estimate.upper_bound_saturated ? '超出可计算范围' : formatCompact(estimate.upper_bound)}
                hint="忽略 NOT 与 OR 相互抵消的保守上界；候选会流式计算，不会全部加载到内存"
              />
              <Metric
                label="预期命中量级"
                value={
                  estimate.upper_bound_saturated
                    ? '—'
                    : `${formatCompact(estimate.expected_matches)}（${formatCompact(estimate.expected_matches_low)} – ${formatCompact(estimate.expected_matches_high)}）`
                }
                hint={`基于 ${formatNumber(estimate.sample_size)} 次均匀抽样的 95% 置信区间`}
              />
              <Metric
                label="抽样命中率"
                value={`${(estimate.hit_rate * 100).toFixed(3)}%`}
              />
              <Metric
                label="待确认占比"
                value={`${(estimate.pending_rate * 100).toFixed(1)}%`}
                hint="需要外部确认的候选比例，直接对应 external_query 消耗"
              />
              <Metric
                label="预计扫描耗时"
                value={formatDuration(estimate.estimated_duration_seconds)}
              />
            </div>

            {!estimate.within_acceptable_error && estimate.hit_rate > 0 && (
              <p className="rounded-md bg-muted p-2 text-[11px] text-muted-foreground">
                抽样相对误差 {(estimate.relative_error * 100).toFixed(0)}% 超出配置容差，
                展示的量级仅供参考。
              </p>
            )}

            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">已前置到生成器的约束</p>
              {estimate.plan.pushed.length === 0 ? (
                <p className="text-xs text-muted-foreground">无（将对全部候选执行本地匹配）</p>
              ) : (
                <ul className="space-y-1">
                  {estimate.plan.pushed.map((p) => (
                    <li key={p.path} className="flex items-center gap-1.5 text-xs">
                      <Badge variant="default" className="text-[10px]">{p.field}</Badge>
                      <span className="text-muted-foreground">{p.operator}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                生成空间：{estimate.plan.alphabet_name} · 长度 {estimate.plan.min_length}–{estimate.plan.max_length} ·
                {' '}{estimate.plan.tlds.length} 个后缀
              </p>
            </div>

            {estimate.warnings?.map((w) => (
              <p key={w} className="flex items-start gap-1.5 rounded-md bg-amber-500/10 p-2 text-[11px] text-amber-800 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                {w}
              </p>
            ))}
          </>
        )}

        {hasExternal && notice && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2.5 text-[11px] text-amber-800 dark:text-amber-300">
            {notice}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b pb-1.5 last:border-0">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        {hint && <p className="text-[10px] text-muted-foreground/70">{hint}</p>}
      </div>
      <span className="shrink-0 text-sm font-medium tabular">{value}</span>
    </div>
  )
}

function usableEstimate(value: Estimate | undefined): Estimate | undefined {
  if (!value?.plan || !Array.isArray(value.plan.pushed) || !Array.isArray(value.plan.tlds)) {
    return undefined
  }
  return value
}

function RuleTestPanel({
  samples, onSamplesChange, onRun, pending, results,
}: {
  samples: string
  onSamplesChange: (value: string) => void
  onRun: () => void
  pending: boolean
  results: TestResult[] | null
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-primary" aria-hidden />
          <CardTitle>即时测试</CardTitle>
          <Badge variant="muted" className="ml-auto">无需保存</Badge>
        </div>
        <CardDescription>每行一个域名，直接测试当前正在编辑的规则，不消耗外部查询额度。</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Textarea
          value={samples}
          rows={4}
          onChange={(event) => onSamplesChange(event.target.value)}
          className="font-mono text-xs"
          aria-label="测试样例域名"
        />
        <Button size="sm" onClick={onRun} disabled={pending}>
          {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
          测试当前规则
        </Button>
        {results && (
          <div className="space-y-2">
            {results.map((result) => <TestResultRow key={result.input} result={result} />)}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TestResultRow({ result }: { result: TestResult }) {
  const [open, setOpen] = React.useState(false)

  if (result.error) {
    return (
      <div className="rounded-md border border-destructive/40 p-2.5 text-xs">
        <span className="font-mono">{result.input}</span>
        <span className="ml-2 text-destructive">{result.error}</span>
      </div>
    )
  }

  const meta = {
    matched: { label: '命中', variant: 'success' as const },
    rejected: { label: '拒绝', variant: 'muted' as const },
    pending_verification: { label: '待确认', variant: 'warning' as const },
  }[result.outcome ?? 'rejected']

  return (
    <div className="rounded-md border p-2.5 text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={open}
      >
        <Badge variant={meta.variant}>{meta.label}</Badge>
        <span className="font-mono">{result.domain ?? result.input}</span>
        <span className="ml-auto text-muted-foreground">{open ? '收起' : '查看证据'}</span>
      </button>

      {result.undetermined?.length ? (
        <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-400">
          待外部确认的节点：{result.undetermined.join('、')}
        </p>
      ) : null}

      {open && result.evidence && (
        <div className="mt-2 border-t pt-2">
          <EvidenceTree node={result.evidence} />
        </div>
      )}
    </div>
  )
}

function EvidenceTree({ node, depth = 0 }: { node: import('@/lib/types').EvidenceNode; depth?: number }) {
  const color =
    node.result === 'true' ? 'text-emerald-700 dark:text-emerald-400'
      : node.result === 'false' ? 'text-red-700 dark:text-red-400'
        : 'text-amber-700 dark:text-amber-400'

  return (
    <div style={{ paddingLeft: depth * 12 }} className="py-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={cn('font-medium', color)}>{resultLabel(node.result)}</span>
        <span className="text-muted-foreground">
          {node.type === 'group'
            ? `${node.not ? 'NOT ' : ''}${node.op} 分组`
            : `${node.not ? 'NOT ' : ''}${node.field} ${node.operator}`}
        </span>
        {node.reason && <span className="text-[10px] text-muted-foreground/80">（{node.reason}）</span>}
      </div>
      {node.children?.map((child, i) => (
        <EvidenceTree key={`${child.path}-${i}`} node={child} depth={depth + 1} />
      ))}
    </div>
  )
}

function resultLabel(result: string): string {
  return { true: '通过', false: '不通过', undetermined: '未决', skipped: '已停用' }[result] ?? result
}

function scrollToRuleError(errorPath: string) {
  const nodePath = errorPath
    .replace(/\.(?:value|field|operator|op|type|not|disabled)(?:\[.*\])?(?:\..*)?$/, '')
    .replace(/\.children$/, '')
  window.requestAnimationFrame(() => {
    const target = document.querySelector<HTMLElement>(`[data-rule-path="${CSS.escape(nodePath)}"]`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })
}

function statusLabel(status: string): string {
  return { draft: '草稿', active: '运行中', paused: '已停用', error: '错误', archived: '已归档' }[status] ?? status
}

/** Resolves the parent group and child index for a JSONPath like `$.root.children[2].children[0]`. */
function resolveParent(root: RuleGroup, path: string): { parent: RuleGroup | null; index: number } {
  const indices = [...path.matchAll(/children\[(\d+)\]/g)].map((m) => Number(m[1]))
  if (indices.length === 0) return { parent: null, index: -1 }

  let current: RuleNode = root
  for (let i = 0; i < indices.length - 1; i++) {
    if (!isGroup(current)) return { parent: null, index: -1 }
    current = current.children[indices[i]]
    if (!current) return { parent: null, index: -1 }
  }
  if (!isGroup(current)) return { parent: null, index: -1 }
  return { parent: current, index: indices[indices.length - 1] }
}

function resolveNode(root: RuleGroup, path: string): RuleNode | null {
  const { parent, index } = resolveParent(root, path)
  if (!parent || index < 0) return null
  return parent.children[index] ?? null
}

/** Finds a length constraint that is mandatory on the positive AND spine. */
function exactLabelLength(group: RuleGroup): number | undefined {
  if (group.disabled || group.not || group.op !== 'AND') return undefined
  for (const node of group.children) {
    if (node.disabled || node.not) continue
    if (isGroup(node)) {
      const nested = exactLabelLength(node)
      if (nested !== undefined) return nested
      continue
    }
    if (node.field === 'label_length' && node.operator === 'EQ') {
      const length = Number(node.value)
      if (Number.isInteger(length) && length > 0) return length
    }
  }
  return undefined
}
